// PlacementSidePanel (UX-17.8) — what is left on the right of the Function screen
// once configuring moved INTO the expanded row.
//
// Alex, 2026-08-01: *"can we get rid of the configuration on the right side and
// directly configure a placement in that extended row?"* … *"I am not really sure I
// need these cost details. And in that case, I would prefer to have it on the top of
// that right side for now. The bottom of that right side is reserved for the future
// AI chat. Which I was planning to add, even if it's not working, to make sure I
// don't forget."*
//
// So: cost breakdown at the TOP, the AI chat Section at the BOTTOM. The chat was a
// deliberately-dead placeholder (a reservation, not a feature) until
// BUILD_BRIEF_assistant_panel.md stage 1 wired it to AssistantSection — its own
// threads/messages, saved in data/assistant.db, calling the Claude API server-side.
//
// The breakdown reads the SERVER-resolved `resolved` already on the placement row, so
// there is no product fetch and no second client-side resolver to keep in step with
// the server's. It refreshes on save rather than previewing live while you type — the
// tradeoff Alex accepted when he said he was not sure he needed the detail.
//
// Props:
//   placement            — the selected placement row, or null
//   money                — the view's money formatter (shared, so this panel cannot drift)
//   onNavigateToProduct  — Where-used row click, "Used in Products"
//   onNavigateToPlacement — Where-used row click, "Placed in Projects"
//
// Where used (2026-08-20) — UsageCards, keyed to the placement's OWN product_id,
// answers "where else does this SAME product appear" from inside the Function
// screen. Below the cost breakdown, in the same scroll region: this panel is
// already "what's on the right for the selected placement," and where-used is
// exactly that, one more fact about the same selection.

import Card from './Card.jsx';
import UsageCards from './UsageCards.jsx';

export default function PlacementSidePanel({ placement, money, onNavigateToProduct, onNavigateToPlacement }) {
  const lines = (placement?.resolved?.lines || []).filter(r => r.line_cost != null);

  // The right region is a STACK of independent cards now (2026-08-20, "can
  // the right panel... be like the middle one" — no shell-owned wrapper, each
  // piece owns its own Card), so this panel renders as a sibling sequence:
  // Cost, then UsageCards' own pair — not one hand-rolled section any more.
  return (
    <>
      <Card storageKey="function:section:cost" title="Cost">
        {!placement ? (
          <div style={{ fontSize: 'var(--fs-small)', color: 'var(--th-text-hint)' }}>
            Select a placement to see its cost breakdown.
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
              <span style={{ fontSize: 'var(--fs-small)', fontWeight: 600, color: 'var(--th-text-body)' }}>
                {placement.label}
              </span>
              <span style={{ fontSize: 'var(--fs-body)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {money(placement.resolved?.total)}
              </span>
            </div>
            {placement.frozen && (
              <div style={{ fontSize: 'var(--fs-micro)', color: 'var(--th-text-hint)', marginBottom: 6 }}>
                Purchased — frozen snapshot, never recomputed.
              </div>
            )}
            {lines.length === 0 ? (
              <div style={{ fontSize: 'var(--fs-small)', color: 'var(--th-text-hint)' }}>No priced components.</div>
            ) : lines.map((l, i) => (
              <div
                key={l.id ?? i}
                style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '2px 0', fontSize: 'var(--fs-small)' }}
              >
                <span style={{ color: 'var(--th-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {l.label}
                </span>
                <span style={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{money(l.line_cost)}</span>
              </div>
            ))}
          </>
        )}
      </Card>
      <UsageCards
        productId={placement?.product_id}
        onNavigateToProduct={onNavigateToProduct}
        onNavigateToPlacement={onNavigateToPlacement}
      />
    </>
  );
}
